/**
 * Pure helpers for mapping a source training session into clone form state.
 */

export function mapManageType(dockerManageType) {
  if (!dockerManageType || dockerManageType === 'aws') {
    return 'cloud';
  }
  if (dockerManageType === 'user') {
    return 'local';
  }
  if (dockerManageType === 'kubernetes') {
    return 'kubernetes';
  }
  return 'cloud';
}

export function getWeightsSessionId(source) {
  const id = source?.params?.trainer_params?.weights_session;
  if (id == null || id === '') {
    return null;
  }
  const numericId = Number(id);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
}

export function shouldUseDefaultWeights(source) {
  return getWeightsSessionId(source) == null;
}

export function getCloneModelParams(source) {
  const modelParams = source?.params?.model_params;
  if (!modelParams || typeof modelParams !== 'object') {
    return {};
  }
  return structuredClone(modelParams);
}

function getSchemaFields(schemaFields) {
  if (!schemaFields) {
    return [];
  }
  if (Array.isArray(schemaFields)) {
    return schemaFields;
  }
  if (Array.isArray(schemaFields.children)) {
    return schemaFields.children;
  }
  return [];
}

function normalizeParamsForSchema(params, fields) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return params;
  }
  if (!Array.isArray(fields) || fields.length === 0) {
    return params;
  }

  const result = { ...params };

  fields.forEach((field) => {
    if (field.type === 'group' && field.children && result[field.name]) {
      result[field.name] = normalizeParamsForSchema(result[field.name], field.children);
    }

    if (field.type !== 'select' || !Array.isArray(field.options)) {
      return;
    }

    const hasOptionChildren = field.options.some((option) => option.children?.length);
    if (!hasOptionChildren) {
      return;
    }

    const fieldName = field.name;
    const value = result[fieldName];

    if (typeof value === 'string') {
      const option = field.options.find((entry) => entry.name === value);
      if (!option?.children) {
        return;
      }

      const nested = {};
      option.children.forEach((childField) => {
        if (result[childField.name] !== undefined) {
          nested[childField.name] = result[childField.name];
          delete result[childField.name];
        }
      });
      result[fieldName] = {
        [value]: normalizeParamsForSchema(nested, option.children),
      };
      return;
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const normalized = {};
      Object.entries(value).forEach(([optionName, optionParams]) => {
        const option = field.options.find((entry) => entry.name === optionName);
        if (option?.children && optionParams && typeof optionParams === 'object') {
          normalized[optionName] = normalizeParamsForSchema(optionParams, option.children);
        } else {
          normalized[optionName] = optionParams;
        }
      });
      result[fieldName] = normalized;
    }
  });

  return result;
}

export function modelParamsToFormState(modelParams, schemaFields) {
  if (!modelParams || typeof modelParams !== 'object') {
    return {};
  }

  const cloned = structuredClone(modelParams);
  const fields = getSchemaFields(schemaFields);
  if (fields.length === 0) {
    return cloned;
  }

  return normalizeParamsForSchema(cloned, fields);
}

export function buildCloneDatasetFields(source, datasetList) {
  const datasetParams = source?.params?.dataset_params;
  const sourceDatasetId = datasetParams?.dataset_id;
  const datasets = Array.isArray(datasetList) ? datasetList : [];
  const dataset = sourceDatasetId
    ? datasets.find((d) => d.id === sourceDatasetId)
    : null;

  if (!sourceDatasetId || !dataset) {
    return { ok: false, sourceDatasetId };
  }

  let annotationSetId = '';
  if (datasetParams?.annotation_set_id && Array.isArray(dataset.annotation_sets)) {
    const annotationSet = dataset.annotation_sets.find(
      (a) => a.id === datasetParams.annotation_set_id,
    );
    annotationSetId = annotationSet?.id || '';
  }

  return {
    ok: true,
    fields: {
      datasetId: dataset.id,
      tagName: datasetParams?.tag_name || '',
      annotationSetId,
      trainGroupName: datasetParams?.train_group_name || '',
      valGroupName: datasetParams?.val_group_name || '',
    },
  };
}

export function buildCloneStaticFields(source) {
  return {
    name: `${source?.name || 'Session'}-COPY`,
    description: source?.description || '',
    schemaType: source?.model || 'modelpack',
    manageType: mapManageType(source?.docker_task?.manage_type),
    useDefaultWeights: shouldUseDefaultWeights(source),
    weightsSessionId: getWeightsSessionId(source),
  };
}
